// Classify the rider's position from pose landmarks so we can pick an
// appropriate drag coefficient (Cd) instead of using a fixed value.
//
// Field-averaged Cd per position (see CD_BY_CLASS below):
//    Upright (hands on tops):        Cd ≈ 1.10
//    Hoods (shallow forward lean):   Cd ≈ 0.90
//    Drops (elbows bent, flat back): Cd ≈ 0.72
//    Aero tuck (torso almost flat):  Cd ≈ 0.58
//    TT / time trial position:       Cd ≈ 0.46
//
// We classify primarily from the torso angle (shoulder → hip vector vs
// vertical) and secondarily from elbow flexion and head position. This
// runs on the client every frame — it's just a few vector operations.

import { Landmark } from './poseDetector';

const LM = {
  NOSE: 0,
  LEFT_SHOULDER: 11,
  RIGHT_SHOULDER: 12,
  LEFT_ELBOW: 13,
  RIGHT_ELBOW: 14,
  LEFT_WRIST: 15,
  RIGHT_WRIST: 16,
  LEFT_HIP: 23,
  RIGHT_HIP: 24,
};

// Wave 20.49cj — `upright` renamed to `tops` (same posture the
// old label described — hands on the tops of the bar, torso near
// vertical). New `standing` for off-saddle climbing / sprinting.
// The pose-only classifier can't detect standing from a single
// side-view frame reliably (needs hip-above-saddle context), so
// `standing` is a valid class in the schema and CdA table but is
// never emitted by classifyPosition() below; it flows in from
// the mobile accelerometer-based StandingDetector for outdoor
// rides.
export type PositionClass =
  | 'standing'
  | 'tops'
  | 'hoods'
  | 'drops'
  | 'aero_tuck'
  | 'tt'
  | 'unknown';

export interface PositionResult {
  class: PositionClass;
  /** Drag coefficient — Track 1.4 returns the *blended* Cd by default.
   *  Continuous in (torsoAngle, elbowAngle), so a rider transitioning from
   *  hoods to drops sees a smooth glide instead of a 0.10 step. Falls back
   *  to the hard-class lookup if any input is unusable. */
  cd: number;
  /** 0..1 how confident we are. */
  confidence: number;
  /** Torso angle from horizontal (degrees). 90 = upright, 0 = flat. */
  torsoAngleDeg: number;
  /** Track 1.4 — soft-max class probabilities. Sums to 1 (or ~0 if
   *  position is `unknown`). Useful for the UI to render a "transitioning
   *  hoods → drops" hint and for the Aero AI to see how *committed* a
   *  position is. */
  classProbs?: Partial<Record<PositionClass, number>>;
  /** Track 1.4 — the hard-class Cd from the lookup table (for diagnostics
   *  and so the server-side fallback can show what the old code path
   *  would have returned). Equal to `cd` when blending isn't available. */
  cdHard?: number;
}

// Cd table — field-averaged, tuned so ADJACENT positions differ by
// ≥15% and the average adjacent delta is ~20%, which matches how
// riders and fitters experience position changes in the real world.
// Reference points (Notio Aerometer aggregate + MyWindsock field
// data 2018-2022): upright→hoods ≈18%, hoods→drops ≈20%, drops→
// aero_tuck ≈19%, aero_tuck→tt ≈21%. Upright→tt ≈58% total.
//
// MUST stay in lockstep with platform/shared/aero/physics.py
// CD_BY_POSITION — the server-side fallback (when cd_blended is
// absent from an older client) reads that table, and any drift
// would silently produce different CdA for the same position.
const CD_BY_CLASS: Record<PositionClass, number> = {
  standing:  1.35,
  tops:      1.10,
  hoods:     0.90,
  drops:     0.72,
  aero_tuck: 0.58,
  tt:        0.46,
  unknown:   0.85,
};

const MIN_VIS = 0.55;

function visible(...lms: Landmark[]): boolean {
  return lms.every((l) => (l.visibility ?? 0) >= MIN_VIS);
}

/** Angle between the shoulder→hip vector and the horizontal axis. */
function computeTorsoAngle(
  shoulder: Landmark,
  hip: Landmark,
  imageWidth: number,
  imageHeight: number
): number {
  // Y axis points down in image coords, so flip.
  const dx = (hip.x - shoulder.x) * imageWidth;
  const dy = (shoulder.y - hip.y) * imageHeight;
  // atan2 returns angle relative to +X axis (horizontal). For a tucked
  // rider leaning forward, the shoulder is above and ahead of the hip —
  // which gives an angle close to 0 (flat). For a fully upright rider,
  // the shoulder is directly above the hip — angle close to 90.
  return Math.abs((Math.atan2(dy, Math.abs(dx) + 1e-6) * 180) / Math.PI);
}

/** Elbow flexion angle at the elbow joint (180 = straight arm). */
function computeElbowAngle(
  shoulder: Landmark,
  elbow: Landmark,
  wrist: Landmark
): number {
  const v1x = shoulder.x - elbow.x;
  const v1y = shoulder.y - elbow.y;
  const v2x = wrist.x - elbow.x;
  const v2y = wrist.y - elbow.y;
  const dot = v1x * v2x + v1y * v2y;
  const mag1 = Math.hypot(v1x, v1y);
  const mag2 = Math.hypot(v2x, v2y);
  if (mag1 === 0 || mag2 === 0) return 180;
  const cos = Math.max(-1, Math.min(1, dot / (mag1 * mag2)));
  return (Math.acos(cos) * 180) / Math.PI;
}

// Track 1.4 — class "centers" in (torsoAngle°, elbowAngle°) space.
// These are the canonical postures for each class; soft-max distance from
// the rider's measured angles to each center gives the blend weights.
// Standing is not represented as a (torsoAngle, elbowAngle) center
// here because a webcam-based side-view of a seated rider is what
// we blend across. Standing arrives via a different signal
// (accelerometer on mobile / off-saddle detection) and gets its
// CdA straight from the CD_BY_CLASS table.
const CLASS_CENTERS: Record<Exclude<PositionClass, 'unknown' | 'standing'>, [number, number]> = {
  tops:      [75, 175],  // torso near vertical, arms straight
  hoods:     [55, 150],  // shallow lean, arms slightly bent
  drops:     [35, 110],  // forward lean, elbows bent ~90°
  aero_tuck: [18,  85],  // deep tuck, elbows tight
  tt:        [ 5, 170],  // flat back, arms extended forward on aerobars
};

/** Soft-max blended Cd across all five position classes given measured angles.
 *  Returns the Cd to use for physics plus per-class probabilities. */
function blendedCd(
  torsoAngleDeg: number,
  elbowAngleDeg: number,
): { cd: number; probs: Record<Exclude<PositionClass, 'unknown' | 'standing'>, number> } {
  // Temperature controls how sharp the soft-max is. Lower → sharper (closer
  // to a hard pick); higher → softer (more blending between adjacent classes).
  // 12° tuned so when the rider is exactly between two class centers (e.g.
  // torso 45° between hoods=55 and drops=35), they get ~45/45/balance with a
  // tiny amount of mass on adjacent classes.
  const TAU = 12.0;

  // Build the unnormalised exponentials.
  const exps: Record<Exclude<PositionClass, 'unknown' | 'standing'>, number> = {
    tops: 0, hoods: 0, drops: 0, aero_tuck: 0, tt: 0,
  };
  let zMax = -Infinity;
  for (const k of Object.keys(CLASS_CENTERS) as Array<Exclude<PositionClass, 'unknown' | 'standing'>>) {
    const [t0, e0] = CLASS_CENTERS[k];
    // Weight torso more than elbow — it's the dominant Cd driver.
    const d = Math.hypot((torsoAngleDeg - t0) / TAU, (elbowAngleDeg - e0) / (TAU * 2));
    const z = -d;
    if (z > zMax) zMax = z;
    exps[k] = z;
  }
  // Normalise via softmax (subtract max for numerical stability).
  let sum = 0;
  for (const k of Object.keys(exps) as Array<Exclude<PositionClass, 'unknown' | 'standing'>>) {
    exps[k] = Math.exp(exps[k] - zMax);
    sum += exps[k];
  }
  let cd = 0;
  for (const k of Object.keys(exps) as Array<Exclude<PositionClass, 'unknown' | 'standing'>>) {
    exps[k] /= sum;
    cd += exps[k] * CD_BY_CLASS[k];
  }
  return { cd, probs: exps };
}

export function classifyPosition(
  landmarks: Landmark[] | null,
  imageWidth: number,
  imageHeight: number
): PositionResult {
  if (!landmarks || landmarks.length < 33) {
    return { class: 'unknown', cd: CD_BY_CLASS.unknown, confidence: 0, torsoAngleDeg: 0 };
  }

  // Pick whichever side has more visible landmarks.
  const left = [
    landmarks[LM.LEFT_SHOULDER],
    landmarks[LM.LEFT_ELBOW],
    landmarks[LM.LEFT_WRIST],
    landmarks[LM.LEFT_HIP],
  ];
  const right = [
    landmarks[LM.RIGHT_SHOULDER],
    landmarks[LM.RIGHT_ELBOW],
    landmarks[LM.RIGHT_WRIST],
    landmarks[LM.RIGHT_HIP],
  ];
  const leftVis = left.reduce((s, l) => s + (l.visibility ?? 0), 0);
  const rightVis = right.reduce((s, l) => s + (l.visibility ?? 0), 0);
  const [shoulder, elbow, wrist, hip] = leftVis >= rightVis ? left : right;

  if (!visible(shoulder, hip)) {
    return { class: 'unknown', cd: CD_BY_CLASS.unknown, confidence: 0, torsoAngleDeg: 0 };
  }

  const torsoAngle = computeTorsoAngle(shoulder, hip, imageWidth, imageHeight);
  const elbowAngle = visible(elbow, wrist)
    ? computeElbowAngle(shoulder, elbow, wrist)
    : 180;

  // Classification thresholds (torso angle from horizontal):
  //    > 65°: upright
  //    45-65°: hoods
  //    25-45°: drops (or hoods with deep lean)
  //    10-25°: aero tuck
  //    < 10°: TT position
  //
  // Elbow flexion further distinguishes drops (bent, ~90-120°) from
  // aero_tuck (very bent, <90°) from hoods (straighter, 130-160°).
  let rawClass: PositionClass;
  if (torsoAngle > 65) {
    rawClass = 'tops';
  } else if (torsoAngle > 45) {
    rawClass = elbowAngle < 130 ? 'drops' : 'hoods';
  } else if (torsoAngle > 25) {
    rawClass = elbowAngle < 110 ? 'aero_tuck' : 'drops';
  } else if (torsoAngle > 10) {
    rawClass = 'aero_tuck';
  } else {
    rawClass = 'tt';
  }

  // Hysteresis: require 5 consecutive frames of the same raw classification
  // before switching. Prevents the *label* from jittering. The hard-class
  // Cd from the lookup table is only used as a fallback when blending
  // inputs are unusable.
  const positionClass = applyHysteresis(rawClass);

  const visMean =
    ((shoulder.visibility ?? 0) + (hip.visibility ?? 0)) / 2;

  // Track 1.4 — continuous Cd via soft-max blend across all five canonical
  // positions. Eliminates the 0.10 step at threshold crossings; the hard
  // class still drives the UI label.
  const blended = blendedCd(torsoAngle, elbowAngle);
  const cdHard = CD_BY_CLASS[positionClass];

  return {
    class: positionClass,
    cd: blended.cd,
    cdHard,
    classProbs: blended.probs,
    confidence: visMean,
    torsoAngleDeg: torsoAngle,
  };
}

// ---- Hysteresis ----
// Only switch position class after N consecutive frames of the same raw
// classification. This prevents the Cd value from jittering between
// adjacent classes (e.g. hoods ↔ drops) due to momentary angle noise.
const HYSTERESIS_FRAMES = 5;
let _lastConfirmedClass: PositionClass = 'unknown';
let _pendingClass: PositionClass = 'unknown';
let _pendingCount = 0;

function applyHysteresis(rawClass: PositionClass): PositionClass {
  if (rawClass === _lastConfirmedClass) {
    // Still in the same class — reset any pending switch
    _pendingClass = rawClass;
    _pendingCount = 0;
    return rawClass;
  }
  if (rawClass === _pendingClass) {
    _pendingCount++;
    if (_pendingCount >= HYSTERESIS_FRAMES) {
      // Confirmed switch
      _lastConfirmedClass = rawClass;
      _pendingCount = 0;
      return rawClass;
    }
  } else {
    // New candidate — start counting
    _pendingClass = rawClass;
    _pendingCount = 1;
  }
  return _lastConfirmedClass;
}

/**
 * Wave 20.49l — true while the classifier is mid-transition: raw
 * class doesn't match the confirmed class AND we haven't hit the
 * hysteresis threshold yet. Callers use this to drop CdA samples
 * during the ambiguous window so the improvement earned in the
 * new position doesn't get charged to the old label's bucket
 * before the label flip.
 */
export function isTransitioning(): boolean {
  return _pendingCount > 0 && _pendingCount < HYSTERESIS_FRAMES;
}

/** Wave 20.49l — reset the module-level hysteresis state. Used on
 *  pipeline unmount and when the rider recaptures the baseline
 *  so state doesn't leak between sessions. */
export function resetHysteresis(): void {
  _lastConfirmedClass = 'unknown';
  _pendingClass = 'unknown';
  _pendingCount = 0;
}

/** Human-readable label for UI display. */
export function labelForPosition(cls: PositionClass): string {
  switch (cls) {
    case 'standing': return 'Standing';
    case 'tops': return 'Tops';
    case 'hoods': return 'Hoods';
    case 'drops': return 'Drops';
    case 'aero_tuck': return 'Aero Tuck';
    case 'tt': return 'Time Trial';
    case 'unknown': return '—';
  }
}
